import React, { useEffect, useState, useLayoutEffect } from 'react';
import { Col, Grid, Title, RSelect } from '@common/components';
import i18n from '@common/config/i18n';

import { useAppDispatch, useAppSelector } from '@app/hooks';
import { DashboardSection, LayoutFilters } from '@dashboard/components';
import ResumeCards from '@dashboard/components/ResumeCards/ResumeCards';

import {
  selectFilter,
  applyFilters,
  shouldApplyFilters,
} from '@dashboard/slices/filters';
import { useResultsStatsMutation } from '@dashboard/api/resultsStats';
import {
  selectStats,
  setStats,
  setCurrentPublication,
} from '@dashboard/slices/resultsStats';
import { setCurrentPublication as setCurrentPublicationAnalysis } from '@dashboard/slices/analysisStats';
import { useTranslation, Trans } from 'react-i18next';
import DateFilters from '@dashboard/components/DateFilters/DateFilters';
import NotResults from '@dashboard/components/NotResults/NotResults';

import { ChartStat } from '@dashboard/components/ChartStat';
import { sortCharts } from '../../util';

import viewClasses from './ResultsStatsView.module.scss';

let lastResults = null;
let currentCall = null;

export default function ResultsStatsView() {
  const dispatch = useAppDispatch();
  const currentStats = useAppSelector(selectStats);
  const currentFilters = useAppSelector(selectFilter);
  const { t } = useTranslation('dashboard\\ResultsStatsView');
  const publicationsT = useTranslation('dashboard\\Publications');
  const applyFiltersSignal = useAppSelector(shouldApplyFilters);

  const [publications, setPublications] = useState([]);

  const [selectedPublication, setSelectedPublication] = useState(
    currentStats.selectedPublication
      ? currentStats.selectedPublication
      : undefined,
  );

  const [resultsStats, { isLoading }] = useResultsStatsMutation();
  const [loading, setLoading] = useState(isLoading);
  const cancelAllRequests = () => {
    if (currentCall) currentCall.abort();
  };

  const filterResults = async () => {
    if (applyFiltersSignal) {
      try {
        cancelAllRequests();
        const q = {
          ...currentFilters,
          publication: currentStats.selectedPublication,
        };
        currentCall = resultsStats(q);
        lastResults = await currentCall.unwrap();
        dispatch(setStats(lastResults.data));
      } catch (ex) {
        if (ex && ex.message === 'Aborted') {
          setLoading(false);
        }
      }
    }
  };

  const handleChangePublication = (pj) => {
    Promise.all([
      dispatch(setCurrentPublication(pj.value)),
      dispatch(setCurrentPublicationAnalysis(pj.value)),
      dispatch(applyFilters()),
    ]);
  };

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    setPublications(
      currentStats.publications
        .map((p) => ({
          label: publicationsT.t(p),
          value: p,
        }))
        .sort((p1, p2) => p1.label.localeCompare(p2.label)),
    );
    if (
      !selectedPublication ||
      selectedPublication.value !== currentStats.selectedPublication
    ) {
      setSelectedPublication({
        label: publicationsT.t(currentStats.selectedPublication),
        value: currentStats.selectedPublication,
      });
    }
  }, [currentStats]);

  useEffect(() => {
    filterResults();
  }, [applyFiltersSignal, currentStats.selectedPublication]);

  useEffect(() => {
    const handleLanguageChanged = () => {
      if (lastResults) {
        setLoading(true);
        setTimeout(() => {
          dispatch(setStats({ ...lastResults.data }));
          setLoading(false);
        });
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  useLayoutEffect(() => {
    return () => {
      cancelAllRequests();
    };
  }, []);

  return (
    <LayoutFilters>
      <section className={viewClasses.dashboard}>
        <ResumeCards resume={currentStats.resume} />
        <DashboardSection title={t('filtersTitle')}>
          <DateFilters isLoading={loading} />
          <Grid justify="start" align="middle">
            <Col xss={12} xs={12} sm={3}>
              <RSelect
                data={publications}
                val={selectedPublication}
                onChange={(e) => handleChangePublication(e)}
                placeholder={publicationsT.t('selectPublicationLabel')}
              />
            </Col>
            <Col xss={12} xs={12} sm={3} />
            <Col xss={12} xs={12} sm={3} />
          </Grid>
        </DashboardSection>

        <Title type="h2" color="primary" className={viewClasses.chartTitle}>
          {t('evaluationsByUnitsResults')}
        </Title>

        {!loading ? (
          <Grid>
            <Col xss={12} xs={12} sm={12}>
              {currentStats &&
              currentStats.stats &&
              currentStats.stats.length ? (
                [...currentStats.stats]
                  .sort(sortCharts)
                  .sort((stat1, stat2) =>
                    publicationsT
                      .t(stat1.course)
                      .localeCompare(publicationsT.t(stat2.course)),
                  )
                  .map((stat) => (
                    <ChartStat
                      key={stat.course}
                      stat={stat}
                      title={
                        <Trans t={publicationsT.t} i18nKey={stat.course} />
                      }
                    />
                  ))
              ) : (
                <NotResults isLoading={loading} card />
              )}
            </Col>
          </Grid>
        ) : (
          <NotResults isLoading={loading} card />
        )}
      </section>
    </LayoutFilters>
  );
}

ResultsStatsView.propTypes = {};
